import React, { useMemo } from 'react';
import { ReturnItem } from 'services/api/refund';
import styled from '@emotion/styled';
import { Popover } from 'antd';
import Image from 'components/SafeImage';
import { useTranslate } from '@tolgee/react';
import { formatNumber } from 'utils/numberFormat';
import { ItemLink } from 'views/Conversation/Sider/TradeProduct';
import { LinkOutlined } from '@ant-design/icons';
import { regionBuyerDetailHostMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import { openExternalURL } from 'utils/openExternalURL';
import { getImageThumb } from 'lib/utils/platformImage';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useXiapibuyEnable } from 'hooks/useXiapibuyEnable';

const Container = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  flex: 1;
  padding: 5px;
  cursor: pointer;
  .ant-image {
    width: 56px;
    height: 56px;
    margin-right: 8px;
  }
  .more {
    color: #4285f4;
  }
  .desc {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 4px;
    color: var(--fett-text-secondary);
    .name {
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .sku {
      color: var(--fett-text-tertiary);
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: flex;
      .truncate {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .quantity {
        flex: none;
      }
    }
  }
  > section {
    display: none;
  }
  &:hover {
    background: var(--fett-bg-subtle);
    > section {
      display: block;
    }
  }
`;
const PopoverContent = styled.div`
  width: 336px;
  max-height: 260px;
  overflow-y: auto;
  overflow-x: hidden;
`;
interface Props {
  info: ReturnItem[];
  unit: string;
  externalStoreId: string;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
}

export const getMercadoProductLink = (itemId: string, region: General.UpperCaseRegion) => {
  if (['BR', 'AR', 'MX'].includes(region)) {
    /**
     * @see https://www.teambition.com/task/664c42679bc04953c7f6bdd1
     */
    const regionHostMap: Partial<Record<General.UpperCaseRegion, string>> = {
      BR: 'https://produto.mercadolivre.com.br/MLB-',
      AR: 'https://articulo.mercadolibre.com.ar/MLA-',
      MX: 'https://articulo.mercadolibre.com.mx/MLM-',
    };
    const path = itemId.match(/\d+/)?.[0];
    return path ? `${regionHostMap[region]}${path}` : undefined;
  }
};

const Product = (props: Props) => {
  const { info, unit, externalStoreId, platform, region } = props;

  const items = useMemo(() => {
    return info.reduce<
      Pick<
        ReturnItem,
        'itemImage' | 'itemName' | 'skuName' | 'itemPrice' | 'returnQuantity' | 'itemId'
      >[]
    >((res, cur) => {
      if (!cur.returnSubLineItems?.length) {
        res.push(cur);
      } else {
        res.push(
          ...cur.returnSubLineItems.map(
            ({ itemId, itemImage, itemName, skuName, refundAmount }) => {
              return {
                itemId,
                itemImage,
                itemName,
                skuName,
                itemPrice: Number(refundAmount),
                returnQuantity: 1,
              };
            },
          ),
        );
      }
      return res;
    }, []);
  }, [info]);

  const [first, ...rest] = items;
  const { itemImage, itemName, skuName, itemPrice, returnQuantity, itemId } = first;
  const { t } = useTranslate();

  const xiapibuyEnable = useXiapibuyEnable(region);

  const productClickHandler = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (platform === 'SHOPEE') {
      let host = `https://${toLowerCase(region)}.xiapibuy.com`;
      if (!xiapibuyEnable) {
        const r = regionBuyerDetailHostMap[toLowerCase(region)];
        if (r) {
          host = r;
        }
      }

      const link = `${host}/product/${externalStoreId}/${itemId}/`;
      openExternalURL(link, '__blank');
    }

    if (platform === 'LAZADA') {
      const regionHostMap: Partial<Record<General.UpperCaseRegion, string>> = {
        MY: 'https://www.lazada.com.my',
        ID: 'https://www.lazada.co.id',
        SG: 'https://www.lazada.sg',
        PH: 'https://www.lazada.com.ph',
        VN: 'https://www.lazada.vn',
        TH: 'https://www.lazada.co.th',
      };

      const link = `${regionHostMap[region]}/products/-i${itemId}.html`;

      openExternalURL(link, '__blank');
    }

    if (isTiKTokUpperCase(platform)) {
      openExternalURL(`https://shop.tiktok.com/view/product/${itemId}?region=${region}`, '__blank');
    }
    if (platform === 'MERCADO') {
      const url = getMercadoProductLink(itemId, region);
      url && openExternalURL(url);
    }
  };

  return (
    <Container onClick={productClickHandler}>
      <div className="mr-[8px]">
        <Image
          preview
          width={56}
          height={56}
          src={getImageThumb(itemImage[0], platform)}
          onClick={(e) => e.stopPropagation()}
          hasSmallPic="both"
        />
      </div>
      <div className="desc">
        <div className="name">{itemName}</div>
        <div className="sku">
          <span className="truncate">{`[${unit}${formatNumber(itemPrice)}] [${skuName}`}</span>
          <span className="quantity">x {returnQuantity}]</span>
        </div>
        {!!rest.length && (
          <Popover
            placement="bottomLeft"
            content={
              <PopoverContent>
                {rest.map((item) => (
                  <Container key={item.itemId} onClick={productClickHandler}>
                    <div className="mr-[8px]">
                      <Image
                        preview
                        width={56}
                        height={56}
                        src={getImageThumb(item.itemImage[0], platform)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="desc">
                      <div className="name">{item.itemName}</div>
                      <div className="sku">
                        <span className="truncate">{`[${unit}${formatNumber(item.itemPrice)}] [${
                          item.skuName
                        }`}</span>
                        <span className="quantity">x {item.returnQuantity}]</span>
                      </div>
                    </div>
                    <ItemLink>
                      <LinkOutlined />
                    </ItemLink>
                  </Container>
                ))}
              </PopoverContent>
            }
          >
            <div className="more">
              {t('scene.orderWorkspace.returnRefundTab.list.column.moreProducts', {
                count: rest.length,
              })}
            </div>
          </Popover>
        )}
      </div>
      <ItemLink>
        <LinkOutlined />
      </ItemLink>
    </Container>
  );
};

export default Product;
